import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Notifications from './notifications/Notifications';
import Footer from './Footer';
import Logo from './Logo';
import { loginAbort, loginUser, preLoginUser } from '../actions/authentication';
import { get2FAResets, getPasswordResetToken, getPreAuthenticationToken, getPublicConfig } from '../reducers';
import './_styles_/LoginPanel.css';
import RequestPasswordResetPopup from './popups/RequestPasswordResetPopup';
import Request2FAResetPopup from './popups/Request2FAResetPopup';
import ResetPasswordPopup from './popups/ResetPasswordPopup';
import Reset2FAPopup from './popups/Reset2FAPopup';
import { onActionKey } from '../utils';
import { parseReset2faToken, parseResetToken } from '../utils/queryString';
import { checkPwdResetLink, resetPasswordTokenInvalidated } from '../actions/passwordResets';
import { reset2FATokenInvalidated, storeReset2FAToken } from '../actions/twoFAResets';
import TextField from './fields/TextField';
import PasswordField from './fields/PasswordField';
import EnvTitle from './EnvTitle';
import DBGLoginLink from './DBGLoginLink';
import useSingleValidator from '../hooks/useSingleValidator';
import loginIdValidator from '../validators/login';
import CheckboxField from './fields/CheckboxField';

function LoginPanel() {
    const { t } = useTranslation();
    const publicConfig = useSelector(getPublicConfig);
    const preAuthenticationToken = useSelector(getPreAuthenticationToken);
    const dispatch = useDispatch();
    const [userId, userIdChangeHandler] = useState('');
    const [userPassword, userPasswordChangeHandler] = useState('');
    const [totpToken, totpTokenChangeHandler] = useState('');
    const [isTrustDevice, setTrustDevice] = useState(false);
    const [isLostDialogVisible, setLostDialogVisible] = useState(false);
    const [is2FALostDialogVisible, set2FALostDialogVisible] = useState(false);
    const history = useHistory();
    const location = useLocation();
    const parsedResetToken = parseResetToken(location.search);
    if (parsedResetToken) {
        dispatch(checkPwdResetLink(parsedResetToken));
        history.push('?');
    }
    const parsedReset2fa = parseReset2faToken(location.search);
    if (parsedReset2fa) {
        dispatch(storeReset2FAToken(parsedReset2fa));
        history.push('?');
    }
    const resetToken = useSelector(getPasswordResetToken);
    const reset2FA = useSelector(get2FAResets);
    const onLoginAbort = () => {
        dispatch(loginAbort());
    };
    const onSubmit = (event) => {
        if (!preAuthenticationToken) {
            dispatch(preLoginUser(userId, userPassword));
        } else {
            dispatch(loginUser(preAuthenticationToken, totpToken, isTrustDevice));
        }

        event.preventDefault();
    };
    const showLostDialog = () => setLostDialogVisible(true);
    const hideLostDialog = () => setLostDialogVisible(false);
    const show2FALostDialog = () => set2FALostDialogVisible(true);
    const hide2FALostDialog = () => set2FALostDialogVisible(false);
    const hideResetDialog = () => dispatch(resetPasswordTokenInvalidated());
    const hide2FAResetDialog = () => dispatch(reset2FATokenInvalidated());

    const isInternalLoginAllowed = !publicConfig.ssoEnabled;
    const [isDisabled, setIsDisabled] = useState(false);
    const { validator, isValid } = useSingleValidator(true, useMemo(() => loginIdValidator(t, isInternalLoginAllowed), [t, isInternalLoginAllowed]));
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    return (
        <>
            {!resetToken && !reset2FA.resetToken && !isLostDialogVisible && !is2FALostDialogVisible && <Notifications />}
            <div className="login-container">
                <div className="login-header">
                    <Logo />
                    <span className="login-title">{publicConfig.appName}</span>
                </div>
                <form className="login" onSubmit={onSubmit}>
                    <EnvTitle className="login-container--env-title" />
                    {!preAuthenticationToken &&
                        <>
                            <div className="login-input-row">
                                <TextField
                                    label={t('login.user-id')}
                                    inputId="login-user-id"
                                    defaultValue={userId}
                                    onChange={userIdChangeHandler}
                                    autoComplete="username"
                                    placeholder="M7AUSRXX"
                                    useDynamicWidth={false}
                                    validator={validator}
                                />
                            </div>
                            <div className="login-input-row">
                                <PasswordField
                                    label={t('login.password')}
                                    inputId="login-user-password"
                                    defaultValue={userPassword}
                                    onChange={userPasswordChangeHandler}
                                />
                            </div>
                        </>
                    }
                    {preAuthenticationToken &&
                        <>
                            <div className="login-input-row">
                                <PasswordField
                                    label={t('twoFA.token')}
                                    icon="info"
                                    iconTooltip={t('twoFA.token-tooltip')}
                                    inputId="login-token"
                                    className="login-token"
                                    onChange={totpTokenChangeHandler}
                                    useDynamicWidth={false}
                                />
                            </div>
                            <div className="login-input-row">
                                <CheckboxField
                                    inputId="trusted-device"
                                    label={t('login.trusted-device')}
                                    icon="info"
                                    iconTooltip={t('login.trusted-device-tooltip')}
                                    onChange={setTrustDevice}
                                />
                            </div>
                        </>
                    }
                    <div className="login-row login-button-row">
                        {!preAuthenticationToken &&
                            <>
                                <span
                                    className="request-password-reset-link"
                                    onClick={showLostDialog}
                                    onKeyUp={onActionKey(showLostDialog)}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {t('request-password-reset.link')}
                                </span>
                                <span
                                    className="request-2fa-reset-link"
                                    onClick={show2FALostDialog}
                                    onKeyUp={onActionKey(show2FALostDialog)}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {t('request-2fa-reset.link')}
                                </span>
                            </>
                        }
                        {preAuthenticationToken &&
                            <>
                                <span
                                    className="abort-login-link"
                                    onClick={onLoginAbort}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {t('login.abort-login')}
                                </span>
                            </>
                        }
                        <button className="button button-bordered login-button" type="submit" disabled={isDisabled}>
                            <FontAwesomeIcon icon="sign-in-alt" />&nbsp;{t('login.login')}
                        </button>
                    </div>
                    <DBGLoginLink />
                </form>
                <Footer />
            </div>
            {isLostDialogVisible && <RequestPasswordResetPopup hideDialog={hideLostDialog} />}
            {is2FALostDialogVisible && <Request2FAResetPopup hideDialog={hide2FALostDialog} /> }
            {resetToken && <ResetPasswordPopup hideDialog={hideResetDialog} resetToken={resetToken} />}
            {reset2FA.resetToken && <Reset2FAPopup hideDialog={hide2FAResetDialog} />}
        </>
    );
}

export default LoginPanel;
